import { useState, useMemo } from 'react';
import { Node, Edge } from '@xyflow/react';
import { Search, IconButton } from '@carbon/react';
import { Close, List, SortAscending, Star } from '@carbon/icons-react';
import { ConceptNodeData } from './ConceptNode';

interface NodeRow {
  id: string;
  label: string;
  degree: number;
  isRoot: boolean;
}

interface NodeBrowserPanelProps {
  nodes: Node[];
  edges: Edge[];
  onSelectNode: (nodeId: string) => void;
  onClose: () => void;
}

type SortMode = 'degree-desc' | 'label-asc';

export const NodeBrowserPanel = ({
  nodes,
  edges,
  onSelectNode,
  onClose,
}: NodeBrowserPanelProps) => {
  const [query, setQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('degree-desc');

  // Derive a stable row list with degree + root flag.
  const nodeRows = useMemo<NodeRow[]>(() => {
    const degreeMap = new Map<string, number>();
    for (const node of nodes) {
      degreeMap.set(node.id, 0);
    }
    for (const edge of edges) {
      degreeMap.set(edge.source, (degreeMap.get(edge.source) ?? 0) + 1);
      degreeMap.set(edge.target, (degreeMap.get(edge.target) ?? 0) + 1);
    }

    const rows: NodeRow[] = nodes.map((node) => ({
      id: node.id,
      label: (node.data as ConceptNodeData).label ?? node.id,
      degree: degreeMap.get(node.id) ?? 0,
      isRoot: false,
    }));

    // Mark the node with the highest degree as root (tie-break: alphabetical label).
    if (rows.length > 0) {
      rows.sort((a, b) => b.degree - a.degree || a.label.localeCompare(b.label));
      rows[0].isRoot = true;
    }

    return rows;
  }, [nodes, edges]);

  // Filter then sort according to current mode.
  const displayRows = useMemo<NodeRow[]>(() => {
    const lower = query.toLowerCase();
    const filtered = lower
      ? nodeRows.filter((r) => r.label.toLowerCase().includes(lower))
      : nodeRows;

    if (sortMode === 'label-asc') {
      return [...filtered].sort((a, b) => a.label.localeCompare(b.label));
    }
    // degree-desc (default): already sorted when root was computed; re-sort to be safe.
    return [...filtered].sort((a, b) => b.degree - a.degree || a.label.localeCompare(b.label));
  }, [nodeRows, query, sortMode]);

  const toggleSort = () => {
    setSortMode((prev) => (prev === 'degree-desc' ? 'label-asc' : 'degree-desc'));
  };

  return (
    <div className="node-browser-panel">
      {/* Header */}
      <div className="node-browser-panel__header">
        <div className="node-browser-panel__header-left">
          <List size={16} className="node-browser-panel__header-icon" />
          <span className="node-browser-panel__title">Node Browser</span>
          <span className="node-browser-panel__total-badge">{nodes.length}</span>
        </div>
        <IconButton
          kind="ghost"
          size="sm"
          label="Close node browser"
          align="bottom-right"
          onClick={onClose}
          className="node-browser-panel__close"
        >
          <Close size={16} />
        </IconButton>
      </div>

      {/* Search + sort controls */}
      <div className="node-browser-panel__controls">
        <Search
          id="node-browser-search"
          labelText="Search nodes"
          placeholder="Filter nodes…"
          size="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
          className="node-browser-panel__search"
        />
        <button
          className={`node-browser-panel__sort-btn ${sortMode === 'label-asc' ? 'node-browser-panel__sort-btn--active' : ''}`}
          onClick={toggleSort}
          title={sortMode === 'degree-desc' ? 'Sort: most connected first — click for A→Z' : 'Sort: A→Z — click for most connected first'}
          aria-label={sortMode === 'degree-desc' ? 'Sort alphabetically' : 'Sort by relationship count'}
        >
          <SortAscending size={14} />
          <span>{sortMode === 'degree-desc' ? '# ↓' : 'A→Z'}</span>
        </button>
      </div>

      {/* Node list */}
      <div className="node-browser-panel__list" role="listbox" aria-label="Nodes">
        {displayRows.length === 0 && (
          <div className="node-browser-panel__empty">No nodes match your filter.</div>
        )}
        {displayRows.map((row) => (
          <button
            key={row.id}
            className="node-browser-panel__row"
            role="option"
            aria-selected={false}
            onClick={() => onSelectNode(row.id)}
            title={row.label}
          >
            <span className="node-browser-panel__row-label">
              {row.isRoot && (
                <Star
                  size={12}
                  className="node-browser-panel__root-icon"
                  aria-label="Root concept"
                />
              )}
              {row.label}
            </span>
            <span className="node-browser-panel__badge" aria-label={`${row.degree} relationships`}>
              {row.degree}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
